import React, { useEffect, useState } from "react";
import { Box, Modal, Typography, Paper, IconButton, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import {
  ETIQUETA_TIPO,
  ImpactoDocumentos,
  TablaDocumentosImpacto,
  accionDocumentos,
  decisionesIniciales,
  detalleError,
  ejecutarCambioTipo,
  listaDecisiones,
  obtenerImpacto,
  regularizarDocumentos,
  textosCambio,
} from "./ImpactoDocumentosRepresentante";

const DESCRIPCION_REHABILITAR = "Esta acción permitirá al usuario entrar a la plataforma como representante del establecimiento, pero puede ser revertido si usted Rol MINEDUC vuelve a cambiar el Tipo de Representante.";

export default function ModalCambioRepresentante({ open, representante, destino, onClose }) {
  const [documentos, setDocumentos] = useState([]);
  const [decisiones, setDecisiones] = useState({});
  const [cargandoImpacto, setCargandoImpacto] = useState(false);
  const [errorImpacto, setErrorImpacto] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [errorProceso, setErrorProceso] = useState("");
  const [fallidos, setFallidos] = useState([]);
  const [cambioAplicado, setCambioAplicado] = useState(false);

  const origen = representante?.tipo_representante;
  const accion = accionDocumentos(origen, destino);

  useEffect(() => {
    if (!open || !representante) return;
    setDocumentos([]);
    setDecisiones({});
    setErrorImpacto("");
    setErrorProceso("");
    setFallidos([]);
    setProcesando(false);
    setCambioAplicado(false);
    if (!accion) return;

    let vigente = true;
    setCargandoImpacto(true);
    obtenerImpacto(accion, [{ rut: representante.run, rbd: representante.rbd }])
      .then((docs) => {
        if (!vigente) return;
        setDocumentos(docs);
        setDecisiones(decisionesIniciales(docs));
      })
      .catch((err) => vigente && setErrorImpacto(detalleError(err, "Error en petición")))
      .finally(() => vigente && setCargandoImpacto(false));
    return () => {
      vigente = false;
    };
  }, [open, representante, accion]);

  if (!open || !representante) return null;

  const textos = textosCambio(destino, false);
  const descripcion = origen === "Deshabilitado" ? DESCRIPCION_REHABILITAR : textos.descripcion;
  const finalizado = cambioAplicado && (errorProceso || fallidos.length > 0);

  const cerrar = () => {
    if (procesando) return;
    onClose(cambioAplicado);
  };

  const confirmar = async () => {
    setProcesando(true);
    setErrorProceso("");
    try {
      await ejecutarCambioTipo(origen, destino, representante.run, representante.rbd);
    } catch (err) {
      setErrorProceso(detalleError(err, "No fue posible cambiar el tipo de Representante"));
      setProcesando(false);
      return;
    }

    setCambioAplicado(true);
    if (!accion) {
      setProcesando(false);
      onClose(true);
      return;
    }

    try {
      const resultados = await regularizarDocumentos(
        accion,
        [{ rut: representante.run, rbd: representante.rbd }],
        listaDecisiones(documentos, decisiones)
      );
      const conError = resultados.filter((r) => r.status === "Fallido");
      if (conError.length === 0) {
        setProcesando(false);
        onClose(true);
        return;
      }
      setFallidos(conError);
    } catch (err) {
      setErrorProceso(`El Representante fue actualizado, pero no fue posible regularizar sus documentos: ${detalleError(err, "Error en petición")}`);
    }
    setProcesando(false);
  };

  return (
    <Modal
      open={open}
      onClose={cerrar}
      aria-labelledby="modal-cambio-representante"
      sx={{ backdropFilter: "blur(5px)", backgroundColor: "rgba(0, 0, 0, 0.05)" }}
      slotProps={{ backdrop: { sx: { opacity: "0.1!important" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "749px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: 0,
        }}
      >
        <Paper elevation={3} sx={{ p: 3, textAlign: "center", position: "relative", borderRadius: "16px" }}>
          <IconButton sx={{ position: "absolute", top: 8, right: 8 }} onClick={cerrar} disabled={procesando}>
            <CloseIcon />
          </IconButton>

          <GroupOutlinedIcon color="error" sx={{ fontSize: 45 }} />
          <Typography
            id="modal-cambio-representante"
            variant="h5"
            component="h1"
            fontWeight="bold"
            sx={{ fontSize: "1.625rem", lineHeight: "48px", color: "#4D4D4D" }}
          >
            Cambio de tipo de Representante
          </Typography>

          <Typography sx={{ color: "#111", mb: 1 }}>
            <b>{representante.nombres} {representante.apellidos}</b> es {ETIQUETA_TIPO[origen] || origen}
          </Typography>

          {finalizado ? (
            <>
              {errorProceso && (
                <Typography sx={{ color: "error.main", my: 2 }}>{errorProceso}</Typography>
              )}
              {fallidos.length > 0 && (
                <>
                  <Typography sx={{ fontWeight: 700, color: "#111" }}>
                    El Representante fue actualizado, pero estos documentos no pudieron regularizarse:
                  </Typography>
                  <TablaDocumentosImpacto documentos={fallidos} mostrarDetalle />
                </>
              )}
              <Box mt={2}>
                <PageButtonBlack onClick={() => onClose(true)} width="200px">
                  Cerrar
                </PageButtonBlack>
              </Box>
            </>
          ) : (
            <>
              {accion && (
                <ImpactoDocumentos
                  cargando={cargandoImpacto}
                  error={errorImpacto}
                  documentos={documentos}
                  decisiones={decisiones}
                  onDecision={(clave, valor) => setDecisiones((prev) => ({ ...prev, [clave]: valor }))}
                />
              )}

              <Typography sx={{ color: "error.main", fontWeight: 700, fontSize: "1.125rem", mt: 2 }}>
                {textos.pregunta}
              </Typography>
              <Box sx={{ maxWidth: "560px", margin: "0 auto" }}>
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  {descripcion}
                </Typography>
              </Box>

              {errorProceso && (
                <Typography variant="body2" sx={{ color: "error.main", fontWeight: 700, mt: 2 }}>
                  {errorProceso}
                </Typography>
              )}
              {procesando && <LinearProgress sx={{ mt: 2 }} />}

              <Box mt={3} display="flex" justifyContent="center" sx={{ gap: 2 }}>
                <PageButtonWhite onClick={cerrar} width="150px" disabled={procesando}>
                  Cancelar
                </PageButtonWhite>
                <PageButtonBlack onClick={confirmar} width="300px" disabled={procesando || cargandoImpacto}>
                  {textos.boton}
                </PageButtonBlack>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
}
